/**
 * The application shell.
 *
 * Wires the store, the editor and the diagram together and owns the things that
 * belong to neither: layout, dialogs, keyboard shortcuts, import and export,
 * and the live region that tells a screen-reader user what just changed.
 *
 * The two-way link between code and picture lives here, and it is deliberately
 * one-directional at the data level: the editor edits the source, the source
 * produces the graph, and clicking the graph only ever moves the caret. There
 * is no path by which the picture can change the code behind the user's back.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { redo, undo } from '@codemirror/commands';

import { BRANDING } from './config/branding';
import { useAppStore } from './store/appStore';
import { CodeEditor } from './editor';
import type { CodeEditorHandle } from './editor';
import { DiagramView } from './diagram';
import type { DiagramViewHandle } from './diagram';
import { summarizeGraph } from './diagram/outline';
import { Toolbar } from './components/Toolbar';
import { SplitPane } from './components/SplitPane';
import { ProblemsPanel } from './components/ProblemsPanel';
import { OutlinePanel } from './components/OutlinePanel';
import { SettingsDialog } from './components/dialogs/SettingsDialog';
import { ExamplesDialog } from './components/dialogs/ExamplesDialog';
import { HelpDialog } from './components/dialogs/HelpDialog';
import { ExportDialog } from './components/dialogs/ExportDialog';
import { copyShareLink } from './components/dialogs/shareLink';
import { ProjectsDialog } from './components/dialogs/ProjectsDialog';
import { getProjectStore } from './store/appStore';
import { createBestAvailableStore } from './persistence';
import type { ProjectRecord } from './persistence';
import { useTheme, useReducedMotion } from './hooks/useTheme';
import { useIsMobile, useIsLandscape } from './hooks/useMediaQuery';
import { Button, IconButton, useToast } from './components/ui';
import {
  AlertTriangle,
  Code,
  List,
  Maximize,
  Play,
  Workflow,
  ZoomIn,
  ZoomOut,
} from './components/ui/icons';
import { useT, useLocale } from './i18n';
import { buildShareUrl, parseDocument, readFileAsText, readShareStateFromLocation } from './export';
import { countBySeverity } from './language/diagnostics/types';
import type { Diagnostic } from './language/diagnostics/types';
import type { Rect } from './graph/model/types';

type MobileTab = 'code' | 'diagram';
type SidePanel = 'problems' | 'outline' | null;

export function App() {
  const t = useT();
  const locale = useLocale();
  const toast = useToast();

  const store = useAppStore();
  const { preferences, diagnostics, graph, source, stale, selectedNodeId, nodeRanges } = store;

  const editorRef = useRef<CodeEditorHandle>(null);
  const diagramRef = useRef<DiagramViewHandle>(null);

  const [mobileTab, setMobileTab] = useState<MobileTab>('code');
  const [sidePanel, setSidePanel] = useState<SidePanel>(null);
  const [dialog, setDialog] = useState<
    'settings' | 'examples' | 'help' | 'export' | 'projects' | null
  >(null);

  const isMobile = useIsMobile();
  const isLandscape = useIsLandscape();
  const resolvedTheme = useTheme(preferences.theme);
  useReducedMotion(preferences.reduceMotion);

  const counts = useMemo(() => countBySeverity(diagnostics), [diagnostics]);

  /* --- persistence ---------------------------------------------------- */

  useEffect(() => {
    let cancelled = false;
    void createBestAvailableStore().then((backend) => {
      if (!cancelled) store.attachPersistence(backend);
    });
    return () => {
      cancelled = true;
    };
    // Runs once: the store instance is stable for the lifetime of the app.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A tab being hidden is the last reliable moment to persist.
  useEffect(() => {
    const flush = (): void => void store.flushSave();
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', flush);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', flush);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* --- shared links ---------------------------------------------------- */

  useEffect(() => {
    void readShareStateFromLocation(window.location).then((shared) => {
      if (!shared) return;
      store.newProject(shared.source, shared.name);
      // Clear the fragment so a reload does not silently overwrite later work.
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.title = t('app.documentTitle', {
      product: BRANDING.name,
      name: store.projectName || t('projects.untitled'),
    });
  }, [store.projectName, t]);

  /* --- code <-> diagram ------------------------------------------------ */

  const revealNodeSource = useCallback(
    (nodeId: string) => {
      const range = nodeRanges.get(nodeId);
      if (!range) return;
      editorRef.current?.revealRange(range, { select: true });
      if (isMobile) setMobileTab('code');
      editorRef.current?.focus();
    },
    [nodeRanges, isMobile],
  );

  const selectNodeFromDiagram = useCallback(
    (id: string | null) => {
      store.selectNode(id);
      if (!id) return;
      const range = nodeRanges.get(id);
      // Single click follows the selection in the editor without stealing focus.
      if (range) editorRef.current?.revealRange(range);
    },
    [nodeRanges, store],
  );

  const goToDiagnostic = useCallback(
    (diagnostic: Diagnostic) => {
      editorRef.current?.revealRange(diagnostic.range, { select: true });
      editorRef.current?.focus();
      if (isMobile) setMobileTab('code');
    },
    [isMobile],
  );

  /* --- announcements ---------------------------------------------------- */

  const [announcement, setAnnouncement] = useState('');
  const lastSummary = useRef('');

  useEffect(() => {
    const summary = summarizeGraph(graph);
    const message =
      summary.nodes === 0
        ? t('a11y.diagramEmpty')
        : t('a11y.diagramUpdated', { nodes: summary.nodes });
    if (message !== lastSummary.current) {
      lastSummary.current = message;
      setAnnouncement(message);
    }
  }, [graph, t]);

  /* --- actions ----------------------------------------------------------- */

  const doFormat = useCallback(() => {
    const changed = editorRef.current?.format() ?? false;
    if (!changed && counts.error > 0) {
      toast.show(t('problems.errors', { count: counts.error }), { tone: 'warning' });
    }
  }, [counts.error, t, toast]);

  const doImport = useCallback(
    async (file: File) => {
      try {
        const text = await readFileAsText(file);
        if (file.name.toLowerCase().endsWith('.json')) {
          const result = parseDocument(text);
          if (!result.ok) {
            toast.show(result.error.message, { tone: 'error' });
            return;
          }
          store.newProject(result.document.project.source, result.document.project.name);
          for (const warning of result.warnings) toast.show(warning, { tone: 'warning' });
        } else {
          store.newProject(text, file.name.replace(/\.[^.]+$/, ''));
        }
        toast.show(t('import.done', { name: file.name }), { tone: 'success' });
      } catch (error) {
        toast.show(
          error instanceof Error
            ? error.message
            : t('import.invalidFile', { product: BRANDING.name }),
          {
            tone: 'error',
          },
        );
      }
    },
    [store, t, toast],
  );

  const doShare = useCallback(async () => {
    const { url, tooLong } = await buildShareUrl(window.location.href, {
      source,
      name: store.projectName,
      direction: preferences.direction,
      functionDisplay: preferences.functionDisplay,
    });
    await copyShareLink(url, tooLong, t, (message, tone) => toast.show(message, { tone }));
  }, [preferences.direction, preferences.functionDisplay, source, store.projectName, t, toast]);

  /* --- keyboard shortcuts ------------------------------------------------ */

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      const mod = event.ctrlKey || event.metaKey;
      const target = event.target as HTMLElement | null;
      const typing =
        target?.isContentEditable || target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA';

      // F1 and F6 are safe while typing; the Ctrl combinations are handled by
      // CodeMirror inside the editor, so they are only claimed outside it.
      if (event.key === 'F1') {
        setDialog('help');
        event.preventDefault();
        return;
      }
      if (event.key === 'F6') {
        const next = store.focusedPanel === 'editor' ? 'diagram' : 'editor';
        store.setFocusedPanel(next);
        if (next === 'editor') editorRef.current?.focus();
        event.preventDefault();
        return;
      }
      if (!mod) return;

      switch (event.key.toLowerCase()) {
        case 'n':
          if (event.shiftKey) return;
          store.newProject('');
          break;
        case 'o':
          setDialog('projects');
          break;
        case 's':
          setDialog('export');
          break;
        case 'enter':
          store.recompileNow();
          break;
        case '0':
          diagramRef.current?.fitToScreen();
          break;
        case '=':
        case '+':
          diagramRef.current?.zoomIn();
          break;
        case '-':
          diagramRef.current?.zoomOut();
          break;
        case 'm':
          if (!event.shiftKey) return;
          setSidePanel((current) => (current === 'problems' ? null : 'problems'));
          break;
        case 'l':
          if (!event.shiftKey) return;
          store.setPreferences({
            panelLayout:
              preferences.panelLayout === 'split'
                ? 'code'
                : preferences.panelLayout === 'code'
                  ? 'diagram'
                  : 'split',
          });
          break;
        case 'd':
          if (!event.shiftKey) return;
          store.setPreferences({ theme: resolvedTheme === 'dark' ? 'light' : 'dark' });
          break;
        default:
          return;
      }
      if (!typing || event.key === '0' || event.key === 'Enter') event.preventDefault();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [preferences.panelLayout, resolvedTheme, store]);

  /* --- panels -------------------------------------------------------------- */

  const editorPanel = (
    <section
      className="flex h-full min-h-0 flex-col bg-surface"
      aria-label={t('a11y.landmarkEditor')}
      onFocus={() => store.setFocusedPanel('editor')}
    >
      <CodeEditor
        ref={editorRef}
        value={source}
        onChange={store.setSource}
        diagnostics={diagnostics}
        functionNames={store.functionNames}
        dark={resolvedTheme === 'dark'}
        fontSize={preferences.editorFontSize}
        fontFamily={preferences.editorFontFamily}
        lineWrapping
        onCursorChange={store.setCursorOffset}
        ariaLabel={t('editor.label')}
        className="flex-1"
      />
      <StatusBar
        source={source}
        errors={counts.error}
        warnings={counts.warning}
        stale={stale}
        autoUpdate={preferences.autoUpdate}
        onUpdateNow={store.recompileNow}
        onToggleProblems={() => setSidePanel((c) => (c === 'problems' ? null : 'problems'))}
        onToggleOutline={() => setSidePanel((c) => (c === 'outline' ? null : 'outline'))}
        t={t}
      />
    </section>
  );

  const diagramPanel = (
    <section
      className="relative flex h-full min-h-0 flex-col"
      aria-label={t('a11y.landmarkDiagram')}
      onFocus={() => store.setFocusedPanel('diagram')}
    >
      {stale ? (
        <p className="flex shrink-0 items-center gap-2 border-b border-border bg-warning-soft px-3 py-1.5 text-xs text-warning">
          <AlertTriangle className="size-4 shrink-0" />
          {t('diagram.staleWarning')}
        </p>
      ) : null}

      {graph.truncated ? (
        <p className="flex shrink-0 items-center gap-2 border-b border-border bg-warning-soft px-3 py-1.5 text-xs text-warning">
          <AlertTriangle className="size-4 shrink-0" />
          {t('diagram.truncatedWarning')}
        </p>
      ) : null}

      <div className="relative min-h-0 flex-1">
        <DiagramView
          ref={diagramRef}
          graph={graph}
          selectedNodeId={selectedNodeId}
          selectedEdgeId={store.selectedEdgeId}
          showGrid={preferences.showGrid}
          showMinimap={preferences.showMinimap}
          reducedMotion={preferences.reduceMotion}
          labels={{
            canvas: t('diagram.label'),
            minimap: t('diagram.minimap'),
            emptyTitle: t('diagram.emptyTitle'),
            emptyBody: t('diagram.emptyBody'),
          }}
          onSelectNode={selectNodeFromDiagram}
          onSelectEdge={store.selectEdge}
          onActivateNode={revealNodeSource}
        />

        <div className="absolute top-3 left-3 flex flex-col gap-1 rounded-lg border border-border bg-surface/90 p-1 shadow-panel backdrop-blur-sm">
          <IconButton
            label={t('diagram.zoomIn')}
            variant="ghost"
            compact
            icon={<ZoomIn />}
            onClick={() => diagramRef.current?.zoomIn()}
          />
          <IconButton
            label={t('diagram.zoomOut')}
            variant="ghost"
            compact
            icon={<ZoomOut />}
            onClick={() => diagramRef.current?.zoomOut()}
          />
          <IconButton
            label={t('diagram.fitToScreen')}
            variant="ghost"
            compact
            icon={<Maximize />}
            onClick={() => diagramRef.current?.fitToScreen()}
          />
        </div>
      </div>
    </section>
  );

  const sidePanelContent =
    sidePanel === 'problems' ? (
      <ProblemsPanel diagnostics={diagnostics} onSelect={goToDiagnostic} t={t} />
    ) : sidePanel === 'outline' ? (
      <OutlinePanel
        graph={graph}
        selectedNodeId={selectedNodeId}
        onSelect={revealNodeSource}
        t={t}
      />
    ) : null;

  /* --- layout ---------------------------------------------------------------- */

  const showEditor = preferences.panelLayout !== 'diagram';
  const showDiagram = preferences.panelLayout !== 'code';

  const desktopBody =
    showEditor && showDiagram ? (
      <SplitPane
        className="h-full"
        first={preferences.swapPanels ? diagramPanel : editorPanel}
        second={preferences.swapPanels ? editorPanel : diagramPanel}
        ratio={preferences.splitRatio}
        onRatioChange={(splitRatio) => store.setPreferences({ splitRatio })}
        handleLabel={t('panel.resizeHandle')}
      />
    ) : showEditor ? (
      editorPanel
    ) : (
      diagramPanel
    );

  const mobileBody = (
    <div className="flex h-full min-h-0 flex-col">
      {isLandscape ? (
        <SplitPane
          className="h-full"
          orientation="vertical"
          first={editorPanel}
          second={diagramPanel}
          ratio={preferences.splitRatio}
          onRatioChange={(splitRatio) => store.setPreferences({ splitRatio })}
          handleLabel={t('panel.resizeHandle')}
        />
      ) : (
        <>
          <div className="min-h-0 flex-1">{mobileTab === 'code' ? editorPanel : diagramPanel}</div>
          <nav
            className="fs-safe-bottom flex shrink-0 border-t border-border bg-surface"
            aria-label={t('a11y.landmarkMain')}
          >
            <MobileTabButton
              active={mobileTab === 'code'}
              onClick={() => setMobileTab('code')}
              icon={<Code className="size-5" />}
              label={t('panel.code')}
              badge={counts.error > 0 ? counts.error : undefined}
            />
            <MobileTabButton
              active={mobileTab === 'diagram'}
              onClick={() => setMobileTab('diagram')}
              icon={<Workflow className="size-5" />}
              label={t('panel.diagram')}
            />
            <MobileTabButton
              active={sidePanel !== null}
              onClick={() => setSidePanel((c) => (c ? null : 'problems'))}
              icon={<List className="size-5" />}
              label={t('panel.problems')}
            />
          </nav>
        </>
      )}
    </div>
  );

  const visibleRect: Rect | null = diagramRef.current?.getVisibleRect() ?? null;

  return (
    <div className="flex h-full min-h-0 flex-col bg-canvas">
      <a
        href="#fs-main"
        className="fs-sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded focus:bg-accent focus:px-3 focus:py-2 focus:text-accent-ink"
      >
        {t('a11y.skipToEditor')}
      </a>

      <Toolbar
        projectName={store.projectName}
        onProjectNameChange={store.setProjectName}
        saveStatus={store.saveStatus}
        theme={preferences.theme}
        onThemeChange={(theme) => store.setPreferences({ theme })}
        panelLayout={preferences.panelLayout}
        onPanelLayoutChange={(panelLayout) => store.setPreferences({ panelLayout })}
        onNew={() => store.newProject('')}
        onOpenProjects={() => setDialog('projects')}
        onOpenExamples={() => setDialog('examples')}
        onImportFile={(file) => void doImport(file)}
        onExport={() => setDialog('export')}
        onUndo={() => {
          const view = editorRef.current?.getView();
          if (view) undo(view);
        }}
        onRedo={() => {
          const view = editorRef.current?.getView();
          if (view) redo(view);
        }}
        onFormat={doFormat}
        onRelayout={store.recompileNow}
        onOpenSettings={() => setDialog('settings')}
        onOpenHelp={() => setDialog('help')}
        onSwapPanels={() => store.setPreferences({ swapPanels: !preferences.swapPanels })}
        compact={isMobile}
        t={t}
      />

      <main id="fs-main" className="min-h-0 flex-1" aria-label={t('a11y.landmarkMain')}>
        {isMobile ? mobileBody : desktopBody}
      </main>

      {sidePanel && !isMobile ? (
        <aside
          className="h-56 shrink-0 border-t border-border"
          aria-label={
            sidePanel === 'problems' ? t('a11y.landmarkProblems') : t('a11y.landmarkOutline')
          }
        >
          {sidePanelContent}
        </aside>
      ) : null}

      {sidePanel && isMobile && !isLandscape ? (
        <aside
          className="fs-safe-bottom h-64 shrink-0 border-t border-border"
          aria-label={
            sidePanel === 'problems' ? t('a11y.landmarkProblems') : t('a11y.landmarkOutline')
          }
        >
          {sidePanelContent}
        </aside>
      ) : null}

      <p aria-live="polite" className="fs-sr-only">
        {announcement}
      </p>

      <SettingsDialog
        open={dialog === 'settings'}
        onClose={() => setDialog(null)}
        preferences={preferences}
        onChange={store.setPreferences}
        t={t}
      />

      <ExamplesDialog
        open={dialog === 'examples'}
        onClose={() => setDialog(null)}
        onLoad={(example) => store.newProject(example.source, example.titleEn)}
        needsConfirmation={store.dirty && source.trim().length > 0}
        locale={locale}
        t={t}
      />

      <HelpDialog open={dialog === 'help'} onClose={() => setDialog(null)} t={t} />

      <ExportDialog
        open={dialog === 'export'}
        onClose={() => setDialog(null)}
        graph={graph}
        source={source}
        projectId={store.projectId}
        projectName={store.projectName}
        createdAt={store.createdAt}
        updatedAt={store.updatedAt}
        visibleRect={visibleRect}
        onShare={() => void doShare()}
        onNotify={(message, tone) => toast.show(message, { tone })}
        t={t}
      />

      <ProjectsDialog
        open={dialog === 'projects'}
        onClose={() => setDialog(null)}
        store={getProjectStore()}
        currentProjectId={store.projectId}
        onOpen={(record: ProjectRecord) => store.loadProject(record)}
        onCreate={() => store.newProject('')}
        locale={locale}
        t={t}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Small pieces
 * ------------------------------------------------------------------ */

interface StatusBarProps {
  readonly source: string;
  readonly errors: number;
  readonly warnings: number;
  readonly stale: boolean;
  readonly autoUpdate: boolean;
  readonly onUpdateNow: () => void;
  readonly onToggleProblems: () => void;
  readonly onToggleOutline: () => void;
  readonly t: ReturnType<typeof useT>;
}

function StatusBar({
  source,
  errors,
  warnings,
  stale,
  autoUpdate,
  onUpdateNow,
  onToggleProblems,
  onToggleOutline,
  t,
}: StatusBarProps) {
  const lines = useMemo(() => source.split('\n').length, [source]);

  return (
    <div
      className="flex shrink-0 items-center gap-2 border-t border-border bg-surface px-2 py-1 text-xs text-ink-muted"
      role="status"
      aria-label={t('a11y.landmarkStatus')}
    >
      <button
        type="button"
        onClick={onToggleProblems}
        className="rounded px-1.5 py-0.5 transition-colors hover:bg-surface-sunken hover:text-ink"
      >
        {errors > 0 || warnings > 0
          ? t('problems.summary', { errors, warnings })
          : t('problems.none')}
      </button>

      <button
        type="button"
        onClick={onToggleOutline}
        className="rounded px-1.5 py-0.5 transition-colors hover:bg-surface-sunken hover:text-ink"
      >
        {t('panel.outline')}
      </button>

      <span className="flex-1" />

      <span className="hidden sm:inline">{t('editor.lines', { count: lines })}</span>

      {!autoUpdate || stale ? (
        <Button size="sm" variant="secondary" iconStart={<Play />} onClick={onUpdateNow}>
          {t('editor.updateNow')}
        </Button>
      ) : null}
    </div>
  );
}

function MobileTabButton({
  active,
  onClick,
  icon,
  label,
  badge,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  badge?: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={[
        'fs-touch relative flex flex-1 flex-col items-center justify-center gap-0.5 py-1.5 text-[11px] transition-colors',
        active ? 'text-accent' : 'text-ink-muted',
      ].join(' ')}
    >
      {icon}
      <span>{label}</span>
      {badge !== undefined ? (
        <span className="absolute top-1 right-[28%] min-w-4 rounded-full bg-danger px-1 text-[10px] leading-4 font-semibold text-white">
          {badge}
        </span>
      ) : null}
    </button>
  );
}
